'use client';

import { InternalCmsMedia, Media } from '@/payload-types';
import { CheckboxInput, useListDrawerContext, useListQuery, useSelection } from '@payloadcms/ui';
import { useRouter } from 'next/navigation';
import { PaginatedDocs } from 'payload';
import { MouseEvent } from 'react';

import './styles.scss';

import { useEffect, useRef, useState } from 'react';

import { PauseIcon } from '../icons/PauseIcon';
import { PlayIcon } from '../icons/PlayIcon';

interface GridMediaListClientProps {
  collectionSlug: string;
  /** The first page, as the route rendered it. Only used until the list view reports its own data. */
  data: PaginatedDocs<InternalCmsMedia | Media>;
}

const RADIO_INPUT_CLASS = 'radio-input';
const RADIO_SELECTED_CLASS = `${RADIO_INPUT_CLASS}--is-selected`;
const RADIO_LABEL_CLASS = `${RADIO_INPUT_CLASS}__label`;
const RADIO_STYLED_CLASS = `${RADIO_INPUT_CLASS}__styled-radio`;
const DEFAULT_VIEW_LIST_CLASS_SELECTOR = '.collection-list__tables';

const GridMediaListClient = ({ collectionSlug, data: initialData }: GridMediaListClientProps) => {
  // The list view owns the rows. Deleting, searching and paging update this context, not the prop,
  // which the server component captured once — reading the prop leaves deleted items on screen.
  const { data: listData } = useListQuery();
  const data = (listData as PaginatedDocs<Media> | undefined) ?? initialData;

  const [gridSizeModifier, setGridSizeModifier] = useState<'large' | 'small'>('small');
  const [enableDefaultView, setEnableDefaultView] = useState(false);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  const router = useRouter();
  const videoRefsMap = useRef<Map<string, HTMLVideoElement>>(new Map());

  const { selected, setSelection } = useSelection();
  const { isInDrawer, onSelect } = useListDrawerContext();

  const isInDrawerCheck = isInDrawer && typeof onSelect === 'function';

  const handleVideoPlay = (e: MouseEvent<HTMLButtonElement>, itemId: string) => {
    e.stopPropagation();

    if (playingVideoId && playingVideoId !== itemId) {
      const currentVideo = videoRefsMap.current.get(playingVideoId);
      if (currentVideo) {
        currentVideo.pause();
      }
    }

    const videoElement = videoRefsMap.current.get(itemId);
    if (videoElement) {
      videoElement.play();
      videoElement.addEventListener(
        'ended',
        () => {
          setPlayingVideoId(null);
        },
        { once: true },
      );
      setPlayingVideoId(itemId);
    }
  };

  const handleVideoPause = (e: MouseEvent<HTMLButtonElement>, itemId: string) => {
    e.stopPropagation();

    const videoElement = videoRefsMap.current.get(itemId);

    if (videoElement) {
      videoElement.pause();
      setPlayingVideoId(null);
    }
  };

  const handleDrawerSelectMedia = (doc: InternalCmsMedia | Media) => {
    onSelect!({ collectionSlug, doc: doc, docID: doc.id });
  };

  const handleItemSelect = (doc: InternalCmsMedia | Media) => {
    if (isInDrawerCheck) {
      handleDrawerSelectMedia(doc);
      return;
    }
    setSelection(doc.id);
  };

  const handleItemClick = (doc: InternalCmsMedia | Media) => {
    if (isInDrawerCheck) {
      handleDrawerSelectMedia(doc);
      return;
    }
    router.push(`/admin/collections/${collectionSlug}/${doc.id}`);
  };

  useEffect(() => {
    const defaultViewElement = document.querySelector(DEFAULT_VIEW_LIST_CLASS_SELECTOR);
    if (!defaultViewElement) return;

    if (enableDefaultView) {
      defaultViewElement.classList.add('fv-show-default');
    } else {
      defaultViewElement.classList.remove('fv-show-default');
    }

    if (enableDefaultView && playingVideoId) {
      const videoElement = videoRefsMap.current.get(playingVideoId);
      if (videoElement) {
        videoElement.pause();
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPlayingVideoId(null);
    }
  }, [enableDefaultView, playingVideoId]);

  return (
    <>
      <CheckboxInput checked={enableDefaultView} onToggle={() => setEnableDefaultView(!enableDefaultView)} label={'Enable default view'} />
      {!enableDefaultView && (
        <div className="fv-media-grid">
          <div className="fv-media-grid__header">
            Grid size:
            <label htmlFor="grid-size-small">
              <div className={[RADIO_INPUT_CLASS, gridSizeModifier === 'small' && RADIO_SELECTED_CLASS].filter(Boolean).join(' ')}>
                <input
                  type="radio"
                  name="grid-size"
                  value="small"
                  id="grid-size-small"
                  onChange={() => setGridSizeModifier('small')}
                  checked={gridSizeModifier === 'small'}
                />
                <span className={RADIO_STYLED_CLASS} />
                <span className={RADIO_LABEL_CLASS}>Small</span>
              </div>
            </label>
            <label htmlFor="grid-size-large">
              <div className={[RADIO_INPUT_CLASS, gridSizeModifier === 'large' && RADIO_SELECTED_CLASS].filter(Boolean).join(' ')}>
                <input
                  type="radio"
                  name="grid-size"
                  value="large"
                  id="grid-size-large"
                  onChange={() => setGridSizeModifier('large')}
                  checked={gridSizeModifier === 'large'}
                />
                <span className={RADIO_STYLED_CLASS} />
                <span className={RADIO_LABEL_CLASS}>Large</span>
              </div>
            </label>
          </div>
          <div className={`fv-media-grid__body fv-media-grid__body--${gridSizeModifier}`}>
            {data.docs.map((item) => {
              if (!item.thumbnailURL) return;
              const isItemSelected = selected.get(item.id) ?? false;

              return (
                <div key={item.id} className="fv-media-grid__item">
                  <CheckboxInput className="fv-media-grid__checkbox" checked={isItemSelected} onToggle={() => handleItemSelect(item)} />
                  <div role="button" onClick={() => handleItemClick(item)} className="fv-media-grid__button">
                    {item.mimeType?.includes('video') && item.url && (
                      <>
                        {playingVideoId === item.id ? (
                          <button
                            className="fv-media-grid__video-control"
                            type="button"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => handleVideoPause(e, item.id)}
                            aria-label="Pause video"
                          >
                            <PauseIcon aria-hidden="true" />
                          </button>
                        ) : (
                          <button
                            className="fv-media-grid__video-control"
                            type="button"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => handleVideoPlay(e, item.id)}
                            aria-label="Play video"
                          >
                            <PlayIcon aria-hidden="true" />
                          </button>
                        )}
                        <video
                          ref={(el) => {
                            if (el) {
                              videoRefsMap.current.set(item.id, el);
                            } else {
                              videoRefsMap.current.delete(item.id);
                            }
                          }}
                          className="fv-media-grid__image"
                          poster={item.thumbnailURL}
                          controls={false}
                          muted
                        >
                          <source src={item.url} type={item.mimeType} />
                        </video>
                      </>
                    )}

                    {item.mimeType?.includes('image') && item.thumbnailURL && (
                      <>
                        <img src={item.thumbnailURL} alt="Media image" className="fv-media-grid__image" loading="lazy" />
                      </>
                    )}

                    <div className="fv-media-grid__content">
                      <span className="fv-media-grid__meta fv-media-grid__meta--filename">{item.filename}</span>
                      {item.width && item.height && (
                        <span className="fv-media-grid__meta fv-media-grid__meta--dimensions">
                          {item.width}x{item.height}px
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default GridMediaListClient;
